import React from 'react';
import { Upload, FileWarning, Sparkles, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { downloadCsvTemplate } from './csvTemplate';

export default function UploadStep({
  schema,
  moduleKey,
  fileRef,
  parsing,
  parseError,
  dragOver,
  setDragOver,
  onFile,
}) {
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  return (
    <div>
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-xl p-12 text-center transition cursor-pointer',
          dragOver ? 'border-[#CC0000] bg-red-50/30'
            : parseError ? 'border-red-300 bg-red-50/20'
            : 'border-slate-300 hover:border-[#CC0000] hover:bg-red-50/10'
        )}
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
          {parsing ? (
            <div className="w-6 h-6 border-2 border-slate-200 border-t-[#CC0000] rounded-full animate-spin" />
          ) : (
            <Upload className="w-6 h-6 text-slate-500" />
          )}
        </div>
        <div className="text-sm font-bold text-slate-900 mb-1">
          {parsing ? 'Parsing file...' : 'Drop your file here, or click to browse'}
        </div>
        <div className="text-xs text-slate-500">CSV (.csv) or Excel (.xlsx, .xls) format · max 5 MB</div>
        {parseError && (
          <div className="mt-4 mx-auto max-w-md p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2 text-left">
            <FileWarning className="w-4 h-4 text-[#CC0000] flex-shrink-0 mt-0.5" />
            <span className="text-xs text-[#CC0000] font-medium">{parseError}</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expected Excel Columns</div>
            <button
              onClick={(e) => { e.stopPropagation(); downloadCsvTemplate(moduleKey); }}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#CC0000] hover:underline"
            >
              <Download className="w-3 h-3" /> Download template
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {schema.fields.map((f) => (
              <span key={f.key} className={cn(
                'px-2 py-1 rounded-md text-[10px] font-medium border',
                f.mandatory ? 'bg-white border-[#CC0000]/40 text-[#CC0000]' : 'bg-white border-slate-200 text-slate-600'
              )}>
                {f.csv}{f.mandatory && '*'}
              </span>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2">* Mandatory columns</div>
        </div>
        <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-200">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-blue-700 mt-0.5" />
            <div>
              <div className="text-xs font-bold text-blue-900">Smart auto-mapping + JSON Schema</div>
              <p className="text-[11px] text-blue-800 mt-1 leading-relaxed">
                After upload, the system auto-detects your column names and generates a configurable JSON schema.
                Forms across HGS modules render dynamically from this schema.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
